import { useState } from 'react';
import {
  Box,
  Stack,
  Skeleton,
  Alert,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { PermissionGate, Permissions } from '@billda/permissions-ui';
import { useFeature, Features } from '@billda/features-ui';
import { getErrorMessage } from '@billda/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import ConfirmDialog from '../../../../shared/components/ui/ConfirmDialog';
import { fileDetailKeys, useFileDetail } from '../../hooks/useFileDetail';
import { useFileDownload } from '../../hooks/useFileDownload';
import { useDeleteFile } from '../../hooks/useDeleteFile';
import { useRenameFile } from '../../hooks/useFolderMutations';
import { getFileIcon } from './fileIcons';
import { formatFileSize } from './formatFileSize';
import { VersionHistorySection } from './VersionHistorySection';
import { VersionUploadArea } from './VersionUploadArea';

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const dateTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function getContentTypeLabel(contentType: string): string {
  if (contentType === 'application/pdf') return 'PDF Document';
  if (contentType.startsWith('image/')) return 'Image';
  if (contentType.includes('spreadsheet') || contentType.includes('excel'))
    return 'Spreadsheet';
  if (contentType.includes('word') || contentType.includes('document'))
    return 'Document';
  return 'File';
}

function getEventIcon(eventType: string) {
  switch (eventType) {
    case 'FileCreated':
      return <CloudUploadOutlinedIcon sx={{ fontSize: 20 }} />;
    case 'FileDownloaded':
      return <FileDownloadOutlinedIcon sx={{ fontSize: 20 }} />;
    case 'FileDeleted':
      return <DeleteOutlineIcon sx={{ fontSize: 20 }} />;
    default:
      return <InsertDriveFileIcon sx={{ fontSize: 20 }} />;
  }
}

function getEventIconColor(eventType: string): string {
  switch (eventType) {
    case 'FileCreated':
      return 'success.main';
    case 'FileDownloaded':
      return 'primary.main';
    case 'FileDeleted':
      return 'error.main';
    default:
      return 'text.secondary';
  }
}

function getEventLabel(eventType: string): string {
  switch (eventType) {
    case 'FileCreated':
      return 'Uploaded this file';
    case 'FileDownloaded':
      return 'Downloaded this file';
    case 'FileDeleted':
      return 'Deleted this file';
    default:
      return eventType;
  }
}

interface FileDetailPanelProps {
  fileId: string;
  siteId: number;
  onClose: () => void;
}

export function FileDetailPanel({
  fileId,
  siteId,
  onClose,
}: FileDetailPanelProps) {
  const queryClient = useQueryClient();
  const { data, error } = useFileDetail(fileId);
  const download = useFileDownload();
  const deleteFile = useDeleteFile(siteId);
  const renameFile = useRenameFile();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameName, setRenameName] = useState('');
  const isEnhancedFileManager = useFeature(
    Features.Files.IsEnhancedFileManagerEnabled
  );

  const currentVersionNumber =
    data?.versions?.find(v => v.isCurrent)?.versionNumber ?? 1;

  const invalidateDetail = () => {
    queryClient.invalidateQueries({ queryKey: fileDetailKeys.detail(fileId) });
  };

  const openRename = () => {
    if (data) {
      setRenameName(data.name);
      setRenameOpen(true);
    }
  };

  const handleRename = () => {
    const trimmed = renameName.trim();
    if (!trimmed || trimmed === data?.name) {
      return;
    }
    renameFile.mutate(
      { fileId, name: trimmed },
      {
        onSuccess: () => {
          setRenameOpen(false);
          invalidateDetail();
        },
      }
    );
  };

  if (error && !data) {
    return (
      <Box data-testid="file-detail-panel" sx={{ p: 3 }}>
        <Alert severity="error">{getErrorMessage(error)}</Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box data-testid="file-detail-panel" sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={200} />
          <Skeleton variant="rectangular" height={120} />
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      data-testid="file-detail-panel"
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Stack spacing={3}>
          {/* File summary card */}
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                {/* File icon + name */}
                <Box
                  sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}
                >
                  {getFileIcon(data.contentType, 40)}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{ wordBreak: 'break-word', flex: 1 }}
                      >
                        {data.name}
                      </Typography>
                      <PermissionGate permission={Permissions.Files.Write}>
                        <IconButton
                          size="small"
                          onClick={openRename}
                          data-testid="rename-file-button"
                          sx={{ mt: -0.5 }}
                        >
                          <EditOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </PermissionGate>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {getContentTypeLabel(data.contentType)} &middot;{' '}
                      {formatFileSize(data.fileSizeBytes)}
                    </Typography>
                  </Box>
                </Box>

                {/* Metadata rows */}
                <Stack spacing={1}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Uploaded
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {dateFormatter.format(new Date(data.uploadedAt))}
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Uploaded by
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {data.uploadedByName}
                    </Typography>
                  </Box>
                </Stack>

                {/* Site context */}
                <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary">
                    Site
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {data.siteName}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Version History */}
          {isEnhancedFileManager &&
            data.versions &&
            data.versions.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Version History
                </Typography>
                <VersionHistorySection
                  versions={data.versions}
                  fileId={fileId}
                  onVersionChange={invalidateDetail}
                />
              </Box>
            )}

          {/* Upload new version */}
          {isEnhancedFileManager && (
            <PermissionGate permission={Permissions.Files.Write}>
              <VersionUploadArea
                fileId={fileId}
                contentType={data.contentType}
                onUploadComplete={invalidateDetail}
              />
            </PermissionGate>
          )}

          {/* Activity timeline */}
          {data.events.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Activity
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  overflow: 'hidden',
                }}
              >
                {data.events.map((event, index) => (
                  <Box key={index}>
                    <Box
                      data-testid="file-event"
                      sx={{
                        display: 'flex',
                        gap: 2,
                        py: 2,
                        px: 2,
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      {/* Event icon */}
                      <Box
                        sx={{
                          flexShrink: 0,
                          pt: 0.25,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: getEventIconColor(event.eventType),
                        }}
                      >
                        {getEventIcon(event.eventType)}
                      </Box>

                      {/* Event content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: 'text.primary' }}
                        >
                          {getEventLabel(event.eventType)}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          {event.userName} &middot;{' '}
                          {dateTimeFormatter.format(new Date(event.timestamp))}
                        </Typography>
                      </Box>
                    </Box>
                    {index < data.events.length - 1 && (
                      <Box
                        sx={{
                          borderBottom: 1,
                          borderColor: 'divider',
                          mx: 2,
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Paper>
            </Box>
          )}
        </Stack>
      </Box>

      {/* Fixed footer — actions */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Stack spacing={1.5}>
          <Button
            variant="contained"
            startIcon={
              download.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <DownloadIcon />
              )
            }
            onClick={() => download.mutate(fileId)}
            disabled={download.isPending}
            fullWidth
            data-testid="download-file-button"
          >
            {download.isPending
              ? 'Downloading…'
              : isEnhancedFileManager
                ? `Download (v${currentVersionNumber})`
                : 'Download'}
          </Button>
          <PermissionGate permission={Permissions.Files.Write}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setConfirmOpen(true)}
              disabled={deleteFile.isPending}
              fullWidth
              data-testid="delete-file-button"
            >
              Delete
            </Button>
          </PermissionGate>
        </Stack>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete file?"
        message="This file will be moved to the recycle bin."
        confirmText="Delete"
        onConfirm={() => {
          setConfirmOpen(false);
          deleteFile.mutate(fileId, { onSuccess: () => onClose() });
        }}
        onCancel={() => setConfirmOpen(false)}
      />

      <Dialog
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        maxWidth="xs"
        fullWidth
        data-testid="rename-file-dialog"
      >
        <DialogTitle>Rename file</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="File name"
            value={renameName}
            onChange={e => setRenameName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleRename();
              }
            }}
            sx={{ mt: 1 }}
            data-testid="rename-file-input"
            slotProps={{ htmlInput: { maxLength: 255 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRename}
            disabled={
              !renameName.trim() ||
              renameName.trim() === data?.name ||
              renameFile.isPending
            }
            data-testid="rename-file-confirm-button"
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
